import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, Check, Package, Plus, Search, Wrench, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { AppSelect } from '../../../components/AppSelect';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  fetchPickerFolders,
  fetchPickerItems,
  fetchPickerSuppliers,
  mergePickerResults,
  pickerEurFmt,
  pickerNumFmt,
  type PickerFolder,
  type PickerItem,
  type PickerItemKind,
  type PickerSearchMode,
  type PickerSupplier,
} from '../utils/offerPositionPickerData';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const ALL = '__all__';

type KindFilter = 'all' | PickerItemKind;
type SortKey = 'name' | 'article_number' | 'origin' | 'unit' | 'ek' | 'gross_price' | 'minutes';
type SortState = { key: SortKey; dir: 'asc' | 'desc' } | null;
type KindResult = { items: PickerItem[]; total: number; page: number };

const EMPTY: KindResult = { items: [], total: 0, page: 1 };
const fmtCount = (n: number) => Number(n || 0).toLocaleString('de-DE');
const itemKey = (item: PickerItem) => `${item.kind}:${item.id}`;

const KIND_OPTIONS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'leistung', label: 'Leistungen' },
  { value: 'material', label: 'Materialien' },
];

const MODE_OPTIONS: { value: PickerSearchMode; label: string; title: string }[] = [
  { value: 'keyword', label: 'Exakt', title: 'Nur Einträge, die den Suchbegriff wörtlich enthalten' },
  {
    value: 'semantic',
    label: 'Beste Treffer',
    title: 'Sortiert nach Relevanz und findet auch sinnverwandte Begriffe (z. B. „Kabel“ → „Leitung“)',
  },
];

const COLUMNS: { key: SortKey; label: string; className: string; numeric?: boolean }[] = [
  { key: 'name', label: 'Bezeichnung', className: 'min-w-[16rem]' },
  { key: 'article_number', label: 'Art.-Nr.', className: 'w-32' },
  { key: 'origin', label: 'Hersteller / Großhändler', className: 'w-44' },
  { key: 'unit', label: 'Einheit', className: 'w-20' },
  { key: 'ek', label: 'EK', className: 'w-24', numeric: true },
  { key: 'gross_price', label: 'VK', className: 'w-24', numeric: true },
  { key: 'minutes', label: 'Min', className: 'w-16', numeric: true },
];

function compareItems(a: PickerItem, b: PickerItem, key: SortKey): number {
  const av = a[key];
  const bv = b[key];
  // Empty values always last, regardless of direction (handled by caller flipping only real values).
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv), 'de', { numeric: true, sensitivity: 'base' });
}

export function PickerKindBadge({ kind }: { kind: PickerItemKind }) {
  const Icon = kind === 'material' ? Package : Wrench;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-px text-[10px] font-semibold ${
        kind === 'material'
          ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
          : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
      }`}
    >
      <Icon className="h-2.5 w-2.5" />
      {kind === 'material' ? 'Material' : 'Leistung'}
    </span>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string; title?: string }[];
  onChange: (value: T) => void;
  ariaLabel: string;
}) {
  return (
    // h-10 = AppSelect trigger / text input height, so all filter controls line up.
    <div className="flex h-10 rounded-lg border border-app-border bg-app-surface p-1" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={`rounded-md px-3 text-sm font-medium transition-colors ${
            value === o.value ? 'bg-pipedrive-blue text-white shadow-sm' : 'text-app-text-muted hover:bg-app-hover'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function OfferPositionAdvancedSearchDialog({
  open,
  initialQuery,
  onClose,
  onPick,
}: {
  open: boolean;
  initialQuery: string;
  onClose: () => void;
  /** Inserts the item into the document; rejects with a message on failure. */
  onPick: (item: PickerItem) => Promise<void>;
}) {
  const [searchInput, setSearchInput] = React.useState(initialQuery);
  const [query, setQuery] = React.useState('');
  const [kindFilter, setKindFilter] = React.useState<KindFilter>('all');
  const [mode, setMode] = React.useState<PickerSearchMode>('keyword');
  const [materialFolderId, setMaterialFolderId] = React.useState<string | null>(null);
  const [leistungFolderId, setLeistungFolderId] = React.useState<string | null>(null);
  const [supplierId, setSupplierId] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortState>(null);

  const [folders, setFolders] = React.useState<Record<PickerItemKind, PickerFolder[]>>({
    material: [],
    leistung: [],
  });
  const [suppliers, setSuppliers] = React.useState<PickerSupplier[]>([]);
  const [results, setResults] = React.useState<Record<PickerItemKind, KindResult>>({
    material: EMPTY,
    leistung: EMPTY,
  });
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState<PickerItemKind | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [addingKey, setAddingKey] = React.useState<string | null>(null);
  /** How often each row was added during this session of the dialog. */
  const [addedCounts, setAddedCounts] = React.useState<Record<string, number>>({});
  const searchRef = React.useRef<HTMLInputElement | null>(null);

  // Take over the sidebar's text each time the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    setSearchInput(initialQuery);
    setQuery(initialQuery.length >= MIN_QUERY_LENGTH ? initialQuery : '');
    setAddedCounts({});
    setError(null);
    window.requestAnimationFrame(() => searchRef.current?.focus());
    // initialQuery is read only at open time on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void Promise.all([fetchPickerFolders(), fetchPickerSuppliers()]).then(([f, s]) => {
      if (cancelled) return;
      setFolders(f);
      setSuppliers(s);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const trimmed = searchInput.trim();
    const next = trimmed.length >= MIN_QUERY_LENGTH ? trimmed : '';
    if (next === query) return;
    const t = window.setTimeout(() => setQuery(next), next ? SEARCH_DEBOUNCE_MS : 0);
    return () => window.clearTimeout(t);
  }, [open, searchInput, query]);

  const kinds = React.useMemo<PickerItemKind[]>(
    () => (kindFilter === 'all' ? ['leistung', 'material'] : [kindFilter]),
    [kindFilter],
  );

  const requestFor = React.useCallback(
    (kind: PickerItemKind, page: number, signal?: AbortSignal) =>
      fetchPickerItems({
        kind,
        query,
        mode,
        // Folder / Großhändler filters only apply when a single type is selected.
        folderId: kindFilter === kind ? (kind === 'material' ? materialFolderId : leistungFolderId) : null,
        supplierId: kindFilter === 'material' ? supplierId : null,
        page,
        signal,
      }),
    [query, mode, kindFilter, materialFolderId, leistungFolderId, supplierId],
  );

  React.useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    Promise.all(kinds.map((kind) => requestFor(kind, 1, controller.signal)))
      .then((res) => {
        if (controller.signal.aborted) return;
        const next: Record<PickerItemKind, KindResult> = { material: EMPTY, leistung: EMPTY };
        kinds.forEach((kind, i) => {
          next[kind] = { ...res[i], page: 1 };
        });
        setResults(next);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(e instanceof Error && e.message ? e.message : 'Einträge konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, kinds, requestFor]);

  const loadMore = async (kind: PickerItemKind) => {
    const current = results[kind];
    setLoadingMore(kind);
    try {
      const res = await requestFor(kind, current.page + 1);
      setResults((prev) => ({
        ...prev,
        [kind]: { items: [...prev[kind].items, ...res.items], total: res.total, page: current.page + 1 },
      }));
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Weitere Einträge konnten nicht geladen werden.');
    } finally {
      setLoadingMore(null);
    }
  };

  const rows = React.useMemo(() => {
    const all = mergePickerResults(kinds.map((kind) => results[kind].items), mode === 'keyword' ? query : '');
    if (!sort) return all;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...all].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null || bv == null) return compareItems(a, b, sort.key);
      return sign * compareItems(a, b, sort.key);
    });
  }, [kinds, results, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const handlePick = async (item: PickerItem) => {
    const key = itemKey(item);
    setAddingKey(key);
    setError(null);
    try {
      await onPick(item);
      setAddedCounts((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Hinzufügen fehlgeschlagen');
    } finally {
      setAddingKey(null);
    }
  };

  const addedTotal = Object.values(addedCounts).reduce((s, n) => s + n, 0);

  const folderOptions = (kind: PickerItemKind) => {
    const list = folders[kind];
    const total = list.reduce((s, f) => s + f.count, 0);
    return [
      { value: ALL, label: `Alle Ordner (${fmtCount(total)})` },
      ...list.map((f) => ({
        value: f.id,
        label: `${f.name}${f.datanorm ? ' DATANORM' : ''} (${fmtCount(f.count)})`,
      })),
    ];
  };

  const supplierOptions = [
    { value: ALL, label: 'Alle Großhändler' },
    ...suppliers.map((s) => ({ value: s.id, label: `${s.name} (${fmtCount(s.count)})` })),
  ];

  const summary = kinds
    .map((kind) => {
      const r = results[kind];
      const label = kind === 'material' ? 'Materialien' : 'Leistungen';
      return r.total > r.items.length
        ? `${fmtCount(r.items.length)} von ${fmtCount(r.total)} ${label}`
        : `${fmtCount(r.items.length)} ${label}`;
    })
    .join(' · ');

  const moreKinds = kinds.filter((kind) => results[kind].total > results[kind].items.length);

  if (!open) return null;
  // Portal: the picker sits in sidebars / mobile sheets whose styles would trap a fixed overlay.
  return createPortal(
    <AppModal
      open={open}
      onClose={onClose}
      // Above the document editor overlay (z-[250]) and its sheets.
      overlayClassName="z-[280]"
      panelClassName="md:max-w-6xl"
      ariaLabelledBy="offer-position-advanced-search-title"
    >
      <div className="flex h-full min-h-0 flex-col md:h-[82vh]">
        <header className="flex items-start justify-between gap-3 border-b border-app-border px-5 py-3">
          <div>
            <h2 id="offer-position-advanced-search-title" className="text-sm font-semibold text-app-text">
              Erweiterte Suche
            </h2>
            <p className="mt-0.5 text-xs text-app-text-subtle">
              Materialien und Leistungen filtern und direkt ins Dokument übernehmen.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 px-5 pt-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Bezeichnung, Artikelnummer oder Hersteller…"
              aria-label="Suchbegriff"
              autoComplete="off"
              className="h-10 w-full rounded-lg border border-app-border bg-app-surface pl-9 pr-3 text-sm focus:border-pipedrive-blue focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-app-text-subtle">Typ</span>
              <Segmented<KindFilter> value={kindFilter} options={KIND_OPTIONS} onChange={setKindFilter} ariaLabel="Typ" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-app-text-subtle">Suche</span>
              <Segmented<PickerSearchMode> value={mode} options={MODE_OPTIONS} onChange={setMode} ariaLabel="Suchmodus" />
            </div>
            {kindFilter !== 'all' ? (
              <div className="w-56">
                <AppSelect
                  value={(kindFilter === 'material' ? materialFolderId : leistungFolderId) ?? ALL}
                  onChange={(val) => {
                    const next = val === ALL ? null : val;
                    if (kindFilter === 'material') setMaterialFolderId(next);
                    else setLeistungFolderId(next);
                  }}
                  options={folderOptions(kindFilter)}
                  size="sm"
                  aria-label="Ordner"
                />
              </div>
            ) : null}
            {kindFilter === 'material' && suppliers.length > 0 ? (
              <div className="w-56">
                <AppSelect
                  value={supplierId ?? ALL}
                  onChange={(val) => setSupplierId(val === ALL ? null : val)}
                  options={supplierOptions}
                  size="sm"
                  aria-label="Großhändler"
                />
              </div>
            ) : null}
            {kindFilter === 'all' ? (
              <span className="text-[11px] text-app-text-subtle">
                Für Ordner- und Großhändlerfilter einen Typ wählen.
              </span>
            ) : null}
          </div>
        </div>

        {error ? (
          <div className="mx-5 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</div>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col px-5 py-3">
          <p className="mb-2 text-[11px] font-medium text-app-text-subtle">
            {loading ? 'Suche läuft…' : summary}
            {query && !loading ? ` für „${query}“` : ''}
          </p>
          <div className="app-scrollbar min-h-0 flex-1 overflow-auto rounded-lg border border-app-border">
            {loading ? (
              <InlineLoadingState label="Laden…" className="py-16 text-xs" />
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-xs text-app-text-subtle">
                {query ? `Keine Treffer für „${query}“.` : 'Keine Einträge für diese Auswahl.'}
                {query && mode === 'keyword' ? (
                  <button
                    type="button"
                    onClick={() => setMode('semantic')}
                    className="ml-1 font-medium text-pipedrive-blue hover:underline"
                  >
                    „Beste Treffer“ versuchen
                  </button>
                ) : null}
              </div>
            ) : (
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 z-[1] bg-app-surface-muted text-[11px] text-app-text-subtle">
                  <tr className="border-b border-app-border">
                    <th className="w-20 px-2 py-2 text-left font-semibold">Typ</th>
                    {COLUMNS.map((col) => {
                      const active = sort?.key === col.key;
                      return (
                        <th
                          key={col.key}
                          className={`${col.className} px-2 py-2 font-semibold ${col.numeric ? 'text-right' : 'text-left'}`}
                          aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          <button
                            type="button"
                            onClick={() => toggleSort(col.key)}
                            className={`inline-flex items-center gap-1 hover:text-app-text ${active ? 'text-app-text' : ''}`}
                          >
                            {col.label}
                            {active ? (
                              sort.dir === 'asc' ? (
                                <ArrowUp className="h-3 w-3" />
                              ) : (
                                <ArrowDown className="h-3 w-3" />
                              )
                            ) : null}
                          </button>
                        </th>
                      );
                    })}
                    <th className="w-28 px-2 py-2" aria-label="Aktion" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-app-border">
                  {rows.map((item) => {
                    const key = itemKey(item);
                    const busy = addingKey === key;
                    const added = addedCounts[key] ?? 0;
                    return (
                      <tr key={key} className="align-top hover:bg-app-hover/60">
                        <td className="px-2 py-2">
                          <PickerKindBadge kind={item.kind} />
                        </td>
                        <td className="px-2 py-2">
                          <p className="font-medium leading-snug text-app-text">{item.name}</p>
                          {item.description ? (
                            <p className="mt-0.5 line-clamp-1 text-[11px] text-app-text-subtle" title={item.description}>
                              {item.description}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 tabular-nums text-app-text-muted">{item.article_number ?? '–'}</td>
                        <td className="px-2 py-2 text-app-text-muted">{item.origin ?? '–'}</td>
                        <td className="px-2 py-2 text-app-text-muted">{item.unit ?? '–'}</td>
                        <td className="px-2 py-2 text-right tabular-nums text-app-text-muted">
                          {item.ek != null ? pickerEurFmt.format(item.ek) : '–'}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums font-medium text-app-text">
                          {item.gross_price != null ? pickerEurFmt.format(item.gross_price) : '–'}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-app-text-muted">
                          {item.minutes != null && item.minutes > 0 ? pickerNumFmt.format(item.minutes) : '–'}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void handlePick(item)}
                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                              added
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover hover:text-app-text'
                            }`}
                            title={added ? 'Noch einmal hinzufügen' : 'Ins Dokument übernehmen'}
                          >
                            {busy ? (
                              <LoadingSpinner size="sm" />
                            ) : added ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                            {added ? `${added}× hinzugefügt` : 'Hinzufügen'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {!loading && moreKinds.length > 0 ? (
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {moreKinds.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  disabled={loadingMore != null}
                  onClick={() => void loadMore(kind)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
                >
                  {loadingMore === kind ? <LoadingSpinner size="sm" /> : null}
                  Weitere {kind === 'material' ? 'Materialien' : 'Leistungen'} laden
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-app-border px-5 py-3">
          <span className="text-xs text-app-text-subtle">
            {addedTotal > 0
              ? `${fmtCount(addedTotal)} ${addedTotal === 1 ? 'Position' : 'Positionen'} hinzugefügt`
              : 'Positionen werden am Ende der Tabelle eingefügt.'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-pipedrive-blue px-4 py-1.5 text-xs font-semibold text-white hover:opacity-90"
          >
            Fertig
          </button>
        </footer>
      </div>
    </AppModal>,
    document.body,
  );
}
